import { operatorRequiresValue } from "../../../Components/filters/operators";
import { isValidFilterValue } from "../../../Components/filters/FilterService";

/**
 * Normalize a single filter for POST /filtered API bodies.
 */
export function normalizeFilterForApi(field, operator, rawValue) {
  const requiresValue = operatorRequiresValue(operator);
  if (!requiresValue) {
    return { field, operator, value: null };
  }
  if (!isValidFilterValue(rawValue)) return null;

  let filterValue = rawValue;
  if (Array.isArray(filterValue)) {
    filterValue = filterValue.join(",");
  }
  if (operator === "equals" || operator === "not_equals") {
    const parts = String(filterValue)
      .split(",")
      .map((v) => v.trim());
    filterValue = parts.every((v) => /^\d+$/.test(v))
      ? parts.map((v) => Number(v))
      : parts.map((v) => v.toLowerCase());
  } else if (operator === "between" || operator === "not_between") {
    filterValue = String(filterValue)
      .split(",")
      .map((v) => v.trim());
  }
  return { field, operator, value: filterValue };
}

/**
 * Build filters[] from URL search params (and optional FilterBuilder ref fallback).
 */
export function buildFiltersFromSearchParams(searchParams, appliedFiltersRef = null) {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      key.endsWith("_label")
    ) {
      return;
    }

    if (!key.startsWith("filter_")) return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operatorKey = `filter_${field}_operator`;
    const operator = searchParams.get(operatorKey) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    const hasValidValue =
      value && value !== "undefined" && String(value).trim() !== "";

    if (!requiresValue || hasValidValue) {
      const normalized = normalizeFilterForApi(
        field,
        operator,
        requiresValue ? value : null
      );
      if (normalized) filters.push(normalized);
    }
  });

  if (
    filters.length === 0 &&
    appliedFiltersRef &&
    Array.isArray(appliedFiltersRef.current) &&
    appliedFiltersRef.current.length > 0
  ) {
    appliedFiltersRef.current.forEach((f) => {
      const normalized = normalizeFilterForApi(f.field, f.operator, f.value);
      if (normalized) filters.push(normalized);
    });
  }

  return filters;
}

/**
 * Remove stale filter_* URL params (e.g. filter_project=undefined).
 * Returns updated URLSearchParams (mutates copy of current).
 */
export function pruneStaleFilterSearchParams(searchParams) {
  const currentParams = new URLSearchParams(searchParams);
  let needsUpdate = false;

  searchParams.forEach((value, key) => {
    if (
      !key.startsWith("filter_") ||
      key.endsWith("_operator") ||
      key.endsWith("_label") ||
      key.endsWith("_logic")
    ) {
      return;
    }

    const field = key.replace("filter_", "");
    const operatorKey = `filter_${field}_operator`;
    const operator = currentParams.get(operatorKey) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (
      requiresValue &&
      (!value || value === "undefined" || String(value).trim() === "")
    ) {
      currentParams.delete(key);
      currentParams.delete(operatorKey);
      currentParams.delete(`filter_${field}_label`);
      currentParams.delete(`filter_${field}_logic`);
      needsUpdate = true;
    }
  });

  return { params: currentParams, needsUpdate };
}

/**
 * Auth headers for fetch/axios in list pages.
 */
export function getListAuthHeaders() {
  const token = localStorage.getItem("token");
  return {
    Authorization: token ? `Bearer ${token}` : "",
    "Content-Type": "application/json",
  };
}

/**
 * Build POST body for filtered list APIs from URL params + module list config.
 *
 * @param {Object} opts
 * @param {URLSearchParams} opts.searchParams
 * @param {number|string} opts.orgId
 * @param {Object} opts.listConfig - from useModuleListConfig / resolveModuleListConfig
 * @param {React.MutableRefObject} [opts.appliedFiltersRef]
 * @param {Object} [opts.extra] - e.g. organization_person_ids
 */
export function buildListRequestBody({
  searchParams,
  orgId,
  listConfig = {},
  appliedFiltersRef = null,
  extra = {},
}) {
  const filters = buildFiltersFromSearchParams(searchParams, appliedFiltersRef);
  const search = searchParams.get("search");
  const defaultSort = listConfig.defaultSort || {
    field: "created_at",
    direction: "desc",
  };

  const requestBody = {
    organization_id: orgId,
    ...(listConfig.context || {}),
    page: Number.parseInt(searchParams.get("page"), 10) || 1,
    pageSize: Math.min(
      Number.parseInt(searchParams.get("pageSize"), 10) || 10,
      100
    ),
    sort: {
      field: searchParams.get("sortBy") || defaultSort.field,
      direction: searchParams.get("sortOrder") || defaultSort.direction,
    },
    filters,
    ...(search && search.trim() !== "" && { search: search.trim() }),
    ...extra,
  };

  return requestBody;
}

/**
 * Initialize URL search params with defaults from module list config.
 */
export function applyDefaultSearchParams(currentParams, listConfig = {}) {
  const params = new URLSearchParams(currentParams);
  let needsUpdate = false;
  const defaultSort = listConfig.defaultSort || {};

  if (!params.has("page")) {
    params.set("page", "1");
    needsUpdate = true;
  }
  if (!params.has("pageSize")) {
    params.set("pageSize", "10");
    needsUpdate = true;
  }
  if (!params.has("sortBy") && defaultSort.field) {
    params.set("sortBy", defaultSort.field);
    needsUpdate = true;
  }
  if (!params.has("sortOrder") && defaultSort.direction) {
    params.set("sortOrder", defaultSort.direction);
    needsUpdate = true;
  }

  return { params, needsUpdate };
}
